import React, { useEffect, useState } from "react";
import styled, { withTheme } from "styled-components";
import moment from "moment";
import SecondaryButton from "../../ui-library/Buttons/SecondaryButton/SecondaryButton";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import NutritionItem from "../NutritionItem/NutritionItem";
import PieChart from "../../ui-library/PieChart/PieChart";
import {
  dailyTargetIds,
  DateFormat,
  getMacronutrientColor,
} from "../../../modules/constants";
import {
  addOtherSlice,
  decimalPlaces,
  roundMealValue,
} from "../../../helpers/mealsHelper";
import { useTranslation } from "react-i18next";

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  p {
    margin-bottom: 20px;
    & + div {
      margin-bottom: 0px;
    }
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 38px;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const StyledPieChart = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const DailySummary = (props) => {
  const { completeDayData, finishDay, closeModal, dataStats, theme, studyId } =
    props;

  const { t } = useTranslation();

  const [pieChartData, setPieChartData] = useState(null);

  useEffect(() => {
    if (dataStats && !pieChartData) {
      // summing data for charts from day items

      const carbsPie = {
        letter: t("appText.dietDiary.carbs"),
        frequency: roundMealValue(dataStats[1]?.frequency ?? 0),
        color: getMacronutrientColor(
          dailyTargetIds.CARBS_ATTR_ID,
          theme,
          studyId
        ),
        percentage: `${Number(
          roundMealValue(dataStats[1]?.frequency ?? 0)
        ).toFixed(decimalPlaces)}%`,
        info: `${Number(roundMealValue(dataStats[1]?.frequency ?? 0)).toFixed(
          decimalPlaces
        )}% - ${Number(roundMealValue(dataStats[1]?.count ?? 0)).toFixed(
          decimalPlaces
        )}g`,
        subInfo: `(${t("appText.dietDiary.sugar")}: ${Number(
          roundMealValue(dataStats[2]?.count ?? 0)
        ).toFixed(decimalPlaces)}g)`,
        isVisible: true,
      };

      const proteinPie = {
        letter: t("appText.dietDiary.protein"),
        frequency: roundMealValue(dataStats[4]?.frequency ?? 0),
        color: getMacronutrientColor(
          dailyTargetIds.PROTEIN_ATTR_ID,
          theme,
          studyId
        ),
        percentage: `${Number(
          roundMealValue(dataStats[4]?.frequency ?? 0)
        ).toFixed(decimalPlaces)}%`,
        info: `${Number(roundMealValue(dataStats[4]?.frequency ?? 0)).toFixed(
          decimalPlaces
        )}% - ${Number(roundMealValue(dataStats[4]?.count ?? 0)).toFixed(
          decimalPlaces
        )}g`,
        isVisible: true,
      };

      const fatsPie = {
        letter: t("appText.dietDiary.fats"),
        frequency: roundMealValue(dataStats[3]?.frequency ?? 0),
        color: getMacronutrientColor(
          dailyTargetIds.FATS_ATTR_ID,
          theme,
          studyId
        ),
        percentage: `${Number(
          roundMealValue(dataStats[3]?.frequency ?? 0)
        ).toFixed(decimalPlaces)}%`,
        info: `${Number(roundMealValue(dataStats[3]?.frequency ?? 0)).toFixed(
          decimalPlaces
        )}% - ${Number(
          roundMealValue(dataStats[3]?.count ?? 0).toFixed(decimalPlaces)
        )}g`,
        isVisible: true,
      };

      const data = [carbsPie, fatsPie, proteinPie];

      data.push(addOtherSlice(data, theme, studyId, t));

      setPieChartData(data);
    }
  }, [dataStats, pieChartData, setPieChartData, studyId, theme, t]);

  return (
    <ComponentWrapper data-testid="day-summary">
      <p>
        {t("contentText.dietDiary.completeDay.message", {
          date: moment(completeDayData.day).format(DateFormat.EXTENDED_FORMAT),
        })}
      </p>
      {dataStats[0]?.count && (
        <NutritionItem
          value={`${roundMealValue(dataStats[0]?.count).toFixed(
            decimalPlaces
          )} kcal`}
          backgroundColor={props.theme?.lightShadeMagenta}
          titleColor={props.theme?.darkPurple}
        />
      )}
      {pieChartData && (
        <StyledPieChart>
          <PieChart
            series={pieChartData.filter((item) => item.frequency > 0)}
            width={380}
            height={300}
          />
        </StyledPieChart>
      )}

      <p>{t("contentText.dietDiary.completeDay.confirmation")}</p>
      <ActionButtonsContainer>
        <SecondaryButton
          data-testid="day-summary-cancel-button"
          className="mr-2"
          size={"sm"}
          onClick={closeModal}
        >
          {t("appText.cancel")}
        </SecondaryButton>
        <PrimaryButton
          data-testid="day-summary-confirm-button"
          size={"sm"}
          onClick={finishDay}
        >
          {t("appText.confirm")}
        </PrimaryButton>
      </ActionButtonsContainer>
    </ComponentWrapper>
  );
};
export default withTheme(DailySummary);
